import React, { useEffect } from 'react'
import { ElementProps } from '../base/element'
import { IComponent, IDataComponent, IDefaultStyle, IDefaultStyles, ISelectItemArrayFunction, ISelectOptionItem, IStringArrayFunction, IStyles} from '../schemamanager/types'
import { Chips } from '../helpers/chips'
import { EventType } from '../schemamanager/schemaManager'

export interface ISelectComponent extends IDataComponent {
  type: 'select'
  options: string[] | ISelectOptionItem[] | IStringArrayFunction | ISelectItemArrayFunction
  multiselect?: boolean
  placeholder?: string
  styleInput?: IStyles
  styleError?: IStyles
  styleOptions?: IStyles
  styleDisabled?: IStyles
}

export const Select = (props: ElementProps) => {
  const { sm, themeColor, events, data, addProps, validateOnBlur } = props
  const comp = props.comp as ISelectComponent

  const ref: any = React.useRef()
  const options: ISelectOptionItem[] = sm.selectOptionsAsObjects(comp)


  const getDefaultEditValue = () => {
    if (comp.multiselect) {
      return ''
    } else {
      return data.value()
    }
  }

  const getDefaultChipsValue = (): string[] => {
    if (comp.multiselect) {
      return data.value() || []
    } else {
      return []
    }
  }

  const [editValue, setEditValue] = React.useState(getDefaultEditValue())
  const [chipsArray, setChipsArray] = React.useState<string[]>(getDefaultChipsValue())


  const doFocus = (compToFocus: IComponent) => {
    if (compToFocus['field'] === comp.field) {
      ref.current?.focus()
    }
  }

  useEffect(() => {
    sm.on(EventType.FocusComponent, doFocus)
    return () => {
      sm.removeListener(EventType.FocusComponent, doFocus)
    }
  }, [sm])


  const style: IDefaultStyles = {
    main: {
      classes: {
        margin: 'mx-2',
        padding: 'px-3 py-2',
        position: 'relative',
        boxShadow: 'shadow',
        borderWidth: 'border',
        borderRadius: 'rounded',
        lineHeight: 'leading-tight',
        focus: {
          boxShadow: 'focus:shadow-outline',
          outline: 'focus:outline-none'
        }
      },
      class: themeColor().focusWiBorderColor,
    },
    styleInput: {
      classes: {
        gridColumnStartEnd: 'col-start-2',
        width: 'w-full',
        textColor: 'text-gray-700',
        borderWidth: 'border-0',
        appearance: 'appearance-none',
        padding: 'py-1 px-4',
        ringWidth: 'ring-0',
        focus: {
          outline: 'focus:outline-none',
          borderWidth: 'focus:border-0',
          appearance: 'focus:appearance-none',
          boxShadow: 'focus:shadow-none',
          ringWidth: 'focus:ring-0',

        }
      },
      class: 'disabled:bg-gray-50'
    },
    styleOptions: {},
    styleDisabled: {
      class: 'bg-gray-50'
    }
  }

  const styleError: IDefaultStyle = {
    classes: {
      ...style.main.classes,
      borderColor: 'border-red-500'
    }
  }

  const styleMultiselect: IDefaultStyle = {
    classes: {
      ...style.main.classes,
      display: 'grid',
    },
    style: { gridTemplateColumns: 'fit-content(70%) 1fr' }    
  }

  const handleChange = (_editValue: any, _chipsArray: string[]) => {
    let fullValue: any = _editValue
    if (comp.multiselect) {
      const arr: string[] = _chipsArray || []
      if (_editValue) {
        if (_chipsArray.indexOf(_editValue) === -1) {
          _chipsArray.push(_editValue)
        }
        fullValue = [...arr]
      } else {
        fullValue = arr
      }
      setChipsArray([..._chipsArray])
      setEditValue('')
    } else {
      setEditValue(_editValue)
    }
    data.updateValue(fullValue)

    if (events().onChange) {
      events().onChange({ sm, comp, value: fullValue })
    }
  }

  const deleteLastChip = () => {
    if (chipsArray && chipsArray.length > 0) {
      chipsArray.pop()
      handleChange('', chipsArray)
    }
  }

  const onDeleteChip = (chip: string) => {
    const newChips: string[] = chipsArray.filter((i) => i !== chip)
    handleChange('', newChips)
  }

  const onDropChip = (chips: string[]) => {
    handleChange('', chips)
  }

  const handleKeyDown = (event: any) => {
    if (options) {
      // backspace key
      if (event.keyCode === 8) {
        if (comp.multiselect) {
          deleteLastChip()
        }
      }
    }
  }

  const handleBlur = () => {
    validateOnBlur()
  }

  const error = data.error()
  if (error) {
    style.main.classes = styleError.classes
  }

  if (comp.multiselect) {
    style.main.classes = styleMultiselect.classes
    style.main.style = styleMultiselect.style
  }

  const m = props.mergeStyles(style)

  const disabled = sm.IsCompDisabled(comp)
  if (disabled) {
    m.main.className = m.main.className + ' ' + m.styleDisabled.className
  }

  return (
    <div {...m.main}>
      {comp.multiselect && (
        <Chips chipsArray={chipsArray} onDeleteChip={onDeleteChip} onDrop={onDropChip} disabled={disabled}/>
      )}
      <select
        ref={ref}
        type='text'
        placeholder={comp.placeholder}
        {...m.styleInput}
        {...events()}
        value={editValue}
        onChange={(ev: any) => handleChange(ev.target.value, chipsArray)}
        onBlur={handleBlur}
        onKeyDown={(e: any) => handleKeyDown(e)}
        {...addProps()}
      >
        {options.map((o, ind) => {
          const key = comp.field + '_' + o.value + '_' + ind
          const props: any = {...m.styleOptions, ...o.props}
          if (o.value !== null) props.value = o.value

          return (
            <option key={key} {...props}>
              {o.label}
            </option>
          )
        })}
      </select>
    </div>
  )
}
